import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Download, KeyRound, Copy, Check, Monitor, Building2, RefreshCw, Unplug, AlertTriangle, CircleCheck,
  CircleDashed, Loader2,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { syncState, useTally } from "@/context/TallyContext";
import { fmtDate, monthLabel, timeAgo } from "@/lib/format";
import { drcr, useBooks } from "@/lib/books";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Where the Windows installer is published (set at build time; see README).
const AGENT_DOWNLOAD_URL = process.env.REACT_APP_AGENT_DOWNLOAD_URL || "/downloads/SyncBooksAgentSetup.exe";

function Step({ n, title, done, children }) {
  return (
    <div className="flex gap-4 rounded-lg border border-slate-200 bg-white p-5">
      <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold ${
        done ? "bg-emerald-100 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
        {done ? <Check size={16} /> : n}
      </div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="text-[15px] font-semibold text-slate-900">{title}</div>
        {children}
      </div>
    </div>
  );
}

function ActivationCode() {
  const [code, setCode] = useState(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!code) return undefined;
    const tick = () => setLeft(Math.max(0, Math.round((new Date(code.expires_at) - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [code]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tally/activation-codes");
      setCode(data);
      setCopied(false);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code.code);
      setCopied(true);
    } catch {
      /* the code is on screen anyway */
    }
  };

  if (code && left > 0) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <div className="rounded-md border border-slate-300 bg-slate-50 px-4 py-2 font-mono text-xl font-semibold tracking-[0.2em] text-slate-900"
          data-testid="activation-code">
          {code.code}
        </div>
        <Button variant="outline" size="sm" onClick={copy} className="gap-1.5">
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
        </Button>
        <span className="text-xs text-slate-500">
          Valid for {Math.floor(left / 60)}:{String(left % 60).padStart(2, "0")} · works once
        </span>
      </div>
    );
  }
  return (
    <Button onClick={create} disabled={busy} className="gap-1.5 bg-[#408dfb] hover:bg-[#2f7bea]" data-testid="create-code">
      <KeyRound size={15} /> {code ? "Create a new code" : "Create activation code"}
    </Button>
  );
}

function AgentCompanies({ agents, isOwner, onLinked }) {
  const [busy, setBusy] = useState(null);
  // Two open companies can share a GUID (one copied or restored from the
  // other), so the name goes along to say which one is meant.
  const link = async (agentId, c) => {
    setBusy(`${c.guid}|${c.name}`);
    try {
      await api.post("/tally/company", { agent_id: agentId, guid: c.guid, name: c.name });
      toast.success("Connected. The first load starts within a few seconds.");
      onLinked();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(null);
  };
  if (!agents.length) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 size={15} className="animate-spin" /> Waiting for the agent to connect…
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {agents.map((agent) => (
        <div key={agent.id} className="rounded-md border border-slate-200">
          <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5 text-sm">
            <Monitor size={15} className="text-slate-400" />
            <span className="font-medium">{agent.machine_name || "Tally computer"}</span>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
              agent.online ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
              {agent.online ? "online" : `offline · seen ${timeAgo(agent.last_seen_at)}`}
            </span>
            {agent.tally?.status && agent.tally.status !== "CONNECTED" && (
              <span className="text-xs text-amber-700">TallyPrime is not answering — {agent.tally.message || "is it running?"}</span>
            )}
          </div>
          {agent.companies.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">
              No company is open in TallyPrime on this computer. Open your company in Tally.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {agent.companies.map((c) => (
                <div key={`${c.guid}|${c.name}`} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="flex min-w-0 items-center gap-2 text-sm">
                    <Building2 size={15} className="shrink-0 text-slate-400" />
                    <span className="truncate font-medium">{c.name}</span>
                    {c.booksFrom && <span className="text-xs text-slate-500">books from {fmtDate(c.booksFrom)}</span>}
                  </div>
                  {c.shares_guid_with ? (
                    <span className="text-right text-xs text-slate-500">
                      Shares its Tally ID with “{c.shares_guid_with}”, which is already connected
                    </span>
                  ) : c.linked_elsewhere ? (
                    <span className="text-xs text-slate-500">Connected to another organisation</span>
                  ) : isOwner ? (
                    <Button size="sm" disabled={busy !== null} onClick={() => link(agent.id, c)}
                      className="bg-[#408dfb] hover:bg-[#2f7bea]" data-testid={`link-${c.guid}`}>
                      {busy === `${c.guid}|${c.name}` ? "Connecting…" : "Connect"}
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Companies this computer's Tally has open that are not connected yet.
 *
 * One Tally company is one organisation, so connecting another means creating
 * another organisation. The plan's company allowance covers it; extra
 * companies are bought as add-ons, and the server refuses past the ceiling.
 */
function OtherCompanies({ agents, connectedGuid }) {
  const [busy, setBusy] = useState(null);
  const spare = [];
  for (const agent of agents || []) {
    for (const c of agent.companies || []) {
      if (c.guid !== connectedGuid && !c.linked_here && !c.linked_elsewhere && !c.shares_guid_with) spare.push(c);
    }
  }
  if (!spare.length) return null;

  const add = async (c) => {
    setBusy(c.guid);
    try {
      // Creating the organisation switches the session into it, so a full
      // reload lands on the new tenant with every cached view discarded.
      await api.post("/organizations", { name: c.name });
      window.location.assign("/connect");
    } catch (e) {
      toast.error(apiError(e));
      setBusy(null);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <div className="border-b border-slate-100 p-5">
        <div className="text-sm font-semibold text-slate-900">Also open in Tally</div>
        <p className="mt-1 text-xs text-slate-600">
          Each Tally company gets its own space in SyncBooks, and you switch between them from the
          top bar. Your plan covers a set number of companies.
        </p>
      </div>
      <div className="divide-y divide-slate-100">
        {spare.map((c) => (
          <div key={`${c.guid}|${c.name}`} className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="flex min-w-0 items-center gap-2 text-sm">
              <Building2 size={15} className="shrink-0 text-slate-400" />
              <span className="truncate font-medium">{c.name}</span>
              {c.booksFrom && <span className="text-xs text-slate-500">books from {fmtDate(c.booksFrom)}</span>}
            </div>
            <Button size="sm" variant="outline" disabled={busy !== null}
              onClick={() => add(c)} data-testid={`add-company-${c.guid}`}>
              {busy === c.guid ? "Adding…" : "Add this company"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Connected({ company, isOwner, refresh, agents }) {
  const [confirm, setConfirm] = useState(false);
  const state = syncState(company);
  const load = company.initial_load || {};
  const pct = load.months_total ? Math.round((load.months_done / load.months_total) * 100) : 0;

  const resync = async () => {
    try {
      await api.post("/tally/resync");
      toast.success("SyncBooks will read the whole company again.");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const disconnect = async () => {
    try {
      await api.delete("/tally/company", { params: { purge: true } });
      toast.success("Disconnected. The company's data was removed from SyncBooks.");
      refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const statusLine = {
    SYNCED: { icon: CircleCheck, cls: "text-emerald-700", text: "In sync with Tally" },
    LOADING: { icon: RefreshCw, cls: "text-blue-700", text: "Reading your books from Tally" },
    AGENT_OFFLINE: { icon: AlertTriangle, cls: "text-amber-700",
      text: "The agent is offline. Is the Tally computer on, and the SyncBooks service running?" },
    COMPANY_CLOSED: { icon: AlertTriangle, cls: "text-amber-700",
      text: "The company is not open in TallyPrime. Open it in Tally to resume syncing." },
  }[state];
  const StatusIcon = statusLine.icon;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 p-5">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Connected Tally company</div>
            <div className="mt-1 text-xl font-semibold text-slate-900" data-testid="connected-company">{company.name}</div>
            <div className={`mt-2 flex items-center gap-1.5 text-sm ${statusLine.cls}`}>
              <StatusIcon size={15} className={state === "LOADING" ? "animate-spin" : ""} /> {statusLine.text}
            </div>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={resync} className="gap-1.5"><RefreshCw size={14} /> Read everything again</Button>
              <Button variant="outline" size="sm" onClick={() => setConfirm(true)} className="gap-1.5 text-rose-600 hover:text-rose-700">
                <Unplug size={14} /> Disconnect
              </Button>
            </div>
          )}
        </div>

        {state === "LOADING" && (
          <div className="space-y-2 border-b border-slate-100 p-5">
            <div className="flex justify-between text-sm">
              <span>First load: {load.months_done} of {load.months_total || "?"} months</span>
              {load.current_month && (
                <span className="text-slate-500">
                  {load.current_month > thisMonth()
                    ? "checking for post-dated vouchers"
                    : `now reading ${monthLabel(load.current_month)}`}
                </span>
              )}
            </div>
            <Progress value={pct} className="h-2" />
            <div className="text-xs text-slate-500">
              SyncBooks reads one month at a time so TallyPrime stays responsive. You can keep working in Tally.
            </div>
          </div>
        )}

        <dl className="grid gap-x-8 gap-y-3 p-5 text-sm sm:grid-cols-3">
          <Fact label="Books from" value={fmtDate(company.books_from)} />
          <Fact label="Last checked Tally" value={timeAgo(company.last_scan_at)} />
          <Fact label="Last change received" value={timeAgo(company.last_batch_at)} />
          <Fact label="Vouchers received" value={(company.received?.VOUCHERS || 0).toLocaleString("en-IN")} />
          <Fact label="Masters received" value={(company.received?.MASTERS || 0).toLocaleString("en-IN")} />
          <Fact label="Connected on" value={fmtDate(company.linked_at)} />
        </dl>
        {company.last_error && state !== "SYNCED" && (
          <div className="border-t border-slate-100 px-5 py-3 text-xs text-amber-800">Agent reports: {company.last_error}</div>
        )}
      </div>

      {state !== "LOADING" && <FiguresCheck />}

      {isOwner && <OtherCompanies agents={agents} connectedGuid={company.guid} />}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {company.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              SyncBooks stops syncing this company and removes the data it copied from Tally. Nothing in
              TallyPrime is touched. You can connect again at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={disconnect} className="bg-rose-600 hover:bg-rose-700">Disconnect</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Tally's own closing balances against the figures shown here — the proof
 * that what the owner reads is what Tally says. */
function FiguresCheck() {
  const { data } = useBooks("/books/check");
  if (!data) return null;
  const on = data.as_of ? fmtDate(data.as_of) : null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="figures-check">
      <div className="text-[15px] font-semibold text-slate-900">Figures check</div>
      <p className="mt-1 text-sm text-slate-500">
        Every few hours SyncBooks asks TallyPrime for its own ledger balances and compares them with the figures shown here.
      </p>
      {data.ok === null || data.ok === undefined ? (
        <div className="mt-3 flex items-center gap-1.5 text-sm text-slate-600">
          <CircleDashed size={15} /> Tally has not reported its balances yet. The first check runs soon after the first load.
        </div>
      ) : data.ok ? (
        <div className="mt-3 flex items-center gap-1.5 text-sm text-emerald-700">
          <CircleCheck size={15} /> All {data.compared.toLocaleString("en-IN")} ledger balances match Tally as of {on}.
        </div>
      ) : (
        <>
          <div className="mt-3 flex items-start gap-1.5 text-sm text-amber-800">
            <AlertTriangle size={15} className="mt-0.5 shrink-0" />
            <span>
              {data.mismatch_count} of {data.compared.toLocaleString("en-IN")} ledger balances differ from Tally as of {on}.
              SyncBooks is re-checking every voucher against Tally; differences usually clear within the hour.
            </span>
          </div>
          <div className="mt-3 overflow-x-auto rounded-md border border-slate-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2 text-left">Ledger</th>
                  <th className="px-3 py-2 text-right">In Tally</th>
                  <th className="px-3 py-2 text-right">Here</th>
                </tr>
              </thead>
              <tbody>
                {data.mismatches.slice(0, 10).map((m) => (
                  <tr key={m.guid} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2">{m.name}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{drcr(m.tally)}</td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{drcr(m.ours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.mismatch_count > 10 && <div className="mt-2 text-xs text-slate-500">and {data.mismatch_count - 10} more</div>}
        </>
      )}
    </div>
  );
}

/** "YYYY-MM" of today, to tell history from the post-dated months after it. */
const thisMonth = () => new Date().toISOString().slice(0, 7);

const Fact = ({ label, value }) => (
  <div>
    <dt className="text-xs text-slate-500">{label}</dt>
    <dd className="mt-0.5 font-medium text-slate-900">{value}</dd>
  </div>
);

export default function TallyConnect() {
  const { role } = useAuth();
  const { company, agents, loading, refresh, setFastPolling } = useTally();
  const isOwner = role === "OWNER";

  // While the owner is setting up, look for the agent every few seconds.
  useEffect(() => {
    setFastPolling(!company);
    return () => setFastPolling(false);
  }, [company, setFastPolling]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;

  const anyAgent = agents.length > 0;
  const anyCompany = agents.some((a) => a.companies.length > 0);

  return (
    <div className="max-w-3xl space-y-5" data-testid="tally-connect-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{company ? "Tally connection" : "Connect your Tally"}</h1>
        <p className="mt-1 text-sm text-slate-500">
          {company
            ? "SyncBooks keeps a copy of your Tally books up to date. It only reads — nothing is ever changed in Tally."
            : "Three steps, about five minutes. SyncBooks only reads your books — nothing is ever changed in Tally."}
        </p>
      </div>

      {company ? (
        <Connected company={company} isOwner={isOwner} refresh={refresh} agents={agents} />
      ) : !isOwner ? (
        <div className="rounded-lg border border-slate-200 bg-white p-5 text-sm text-slate-600">
          This organisation is not connected to Tally yet. Ask its owner to connect it.
        </div>
      ) : (
        <>
          <Step n={1} title="Install the SyncBooks agent on the computer that runs TallyPrime" done={anyAgent}>
            <a href={AGENT_DOWNLOAD_URL} className="inline-flex">
              <Button variant="outline" className="gap-1.5"><Download size={15} /> Download for Windows</Button>
            </a>
            <div className="rounded-md bg-slate-50 p-3 text-xs leading-relaxed text-slate-600">
              In TallyPrime, open <b>F1 › Settings › Connectivity › Client/Server configuration</b> and set
              <b> TallyPrime acts as: Both</b> on port <b>9000</b>. Keep your company open in Tally.
            </div>
          </Step>
          <Step n={2} title="Create an activation code and paste it into the installer" done={anyAgent}>
            <ActivationCode />
          </Step>
          <Step n={3} title="Choose the Tally company to connect" done={false}>
            {anyAgent || anyCompany ? (
              <AgentCompanies agents={agents} isOwner={isOwner} onLinked={refresh} />
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <CircleDashed size={15} /> The companies open in Tally appear here once the agent is activated.
              </div>
            )}
          </Step>
        </>
      )}
    </div>
  );
}
